import { useCallback } from "react";
import { useIntl } from "react-intl";

import { SearchOption } from "@components/fieldsNative/SearchInput";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMyExpert from "@hooks/useMyExpert";
import {
  ArticleContentTypeEnum,
  GetArticlesSearchDocument,
  GetArticlesSearchQuery,
  GetArticlesSearchQueryVariables
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

const useSearchArticles = (journeyId: string, onClick?: (id: string) => void) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const getArticleSearch = useLazyQueryWithPromise<GetArticlesSearchQuery, GetArticlesSearchQueryVariables>(
    GetArticlesSearchDocument
  );

  const searchOptions = useCallback(
    (v: string) =>
      getArticleSearch({
        variables: {
          contentTypes: [
            ArticleContentTypeEnum.MY_STORY,
            ArticleContentTypeEnum.TEXT,
            ArticleContentTypeEnum.VIDEO,
            ArticleContentTypeEnum.WEBINAR
          ],
          count: 35,
          search: v
        }
      }).then(res => {
        const nodes = (res.data?.articlesSearch ?? []).map(node => ({
          category: intl.formatMessage({ id: `models.explore.articleType.${node.contentType}` }),
          highlight: node.highlight,
          label: node.title,
          link: onClick
            ? undefined
            : journeyRoute({
                journeyId,
                linkAfterJourney: `/articles/${node.id}`,
                myExpert
              }),
          onClick: () => onClick?.(node.id),
          value: node.id
        }));

        const final = nodes.reduce(
          (acc, node) => {
            const category = node.category;
            const existingCategory = acc.find(item => item.label === category);
            if (existingCategory) {
              existingCategory.options.push(node);
            } else {
              acc.push({
                label: category,
                options: [node]
              });
            }
            return acc;
          },
          [] as { label: string; options: SearchOption[] }[]
        );

        return final.sort((a, b) => a.label.localeCompare(b.label));
      }),
    [getArticleSearch, intl, journeyId, myExpert, onClick]
  );

  return searchOptions;
};

export default useSearchArticles;
